import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  CheckCircle2Icon,
  DownloadIcon,
  ExternalLinkIcon,
  FileCheckIcon,
  MoreHorizontalIcon,
  PlusIcon,
  PrinterIcon,
  SendIcon,
  TrashIcon,
  UploadIcon,
  XCircleIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { RoleCondition } from "@/components/RoleCondition";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { StatusIcon } from "@/components/ui/icons";
import {
  Menu,
  MenuItem,
  MenuSeparator,
  MenuSubTrigger,
  MenuTrigger,
} from "@/components/ui/menu";
import { Modal } from "@/components/ui/modal";
import { NavList, NavListItem } from "@/components/ui/nav-list";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useDialogState } from "@/components/ui/utils";
import { getApiUrl } from "@/lib/api";
import type { AccessionRouter } from "@/lib/types";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import { usePrintIframe } from "@/utils/usePrintIframe";
import { useAccession } from "../AccessionProvider";
import { DetailSection } from "./Section";
import { useSetDialogState } from "./state";

type Report = AccessionRouter["reports"][number];
type Upload = AccessionRouter["uploads"]["list"][number];

type ReportItem = Report & { type: "report"; apiUrl: string };
type UploadItem = Omit<Upload, "type"> & { type: "upload"; apiUrl: string };

type CombinedReport = ReportItem | UploadItem;

export function ReportSection() {
  const { accession } = useAccession();
  const navigate = useNavigate();
  const reports = useReports(accession);
  const setUploadDialog = useSetDialogState("upload");

  if (!reports.length) {
    return null;
  }

  return (
    <DetailSection
      title="Reports"
      hideBorder
      badge={<Badge size="sm">{reports.length}</Badge>}
      actions={
        <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
          <div className="flex items-center gap-1">
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="icon-sm"
                onPress={() => {
                  setUploadDialog({ files: [], type: "report" });
                }}
              >
                <UploadIcon />
              </Button>
              <Tooltip>Upload report</Tooltip>
            </TooltipTrigger>
            <TooltipTrigger>
              <Button
                variant="ghost"
                size="icon-sm"
                onPress={() => {
                  navigate({
                    to: "/accessions/$id/report",
                    params: { id: accession.id },
                    state: {
                      href: {
                        to: "/accessions/$id",
                        params: { id: accession.id },
                      },
                    },
                  });
                }}
              >
                <PlusIcon />
              </Button>
              <Tooltip>{`Generate ${
                accession.status === "final" ? "corrected " : ""
              }report`}</Tooltip>
            </TooltipTrigger>
          </div>
        </RoleCondition>
      }
    >
      <NavList items={reports} className="mt-1 border-b-0" aria-label="Reports">
        {(report) => (
          <NavListItem key={report.id} asChild>
            <a
              href={report.apiUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between gap-4 border rounded-md pl-4 pr-2 h-10 shadow-xs cursor-pointer hover:bg-muted/50 [&+&]:mt-1"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Text size="1" truncate className="flex items-center gap-2">
                  <FileCheckIcon className="shrink-0 text-primary size-4" />
                  {report.type === "upload"
                    ? report.name
                    : `report_${report.id}.pdf`}
                </Text>
                {report.type === "report" && report.isCorrected && (
                  <Badge color="red" size="sm" className="inline">
                    C<span className="hidden md:inline">orrected</span>
                  </Badge>
                )}
                {report.type === "report" && report.isPrelim && (
                  <Badge color="yellow" size="sm" className="inline">
                    P<span className="hidden md:inline">reliminary</span>
                  </Badge>
                )}
                {report.type === "report" &&
                  !report.isCorrected &&
                  !report.isPrelim && (
                    <Badge color="indigo" size="sm" className="inline">
                      F<span className="hidden md:inline">inal</span>
                    </Badge>
                  )}
                {report.type === "upload" && (
                  <Badge size="sm" className="inline">
                    E<span className="hidden md:inline">xternal</span>
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Text size="1" mono muted className="shrink-0">
                  {format(report.createdAt)}
                </Text>
                <div className="hidden md:block">
                  <UserAvatar tooltip user={report.createdBy} />
                </div>
                <ReportDropdown report={report} />
              </div>
            </a>
          </NavListItem>
        )}
      </NavList>
    </DetailSection>
  );
}

export function ReportDropdown({ report }: { report: CombinedReport }) {
  const [dialog, setDialog] = useState<null | string>(null);

  const utils = trpc.useUtils();

  const onDialogClose = () => setDialog(null);

  const deleteReport = trpc.reports.delete.useMutation(
    mutationResponse({
      errorTitle: "Failed to delete report",
      successMessage: "Report deleted",
      onSuccess: onDialogClose,
    }),
  );

  const deleteUpload = trpc.accession.uploads.delete.useMutation(
    mutationResponse({
      errorTitle: "Failed to delete report",
      successMessage: "Report deleted",
      onSuccess: onDialogClose,
    }),
  );

  return (
    <>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="icon"
          isPending={deleteReport.isPending}
          onPress={() => {
            if (report.accessionId) {
              utils.accession.reports.invalidate(report.accessionId);
            }
          }}
        >
          <MoreHorizontalIcon />
        </Button>
        <Menu aria-label="Report actions">
          <RoleCondition
            roles={["blis_lab_assistant", "blis_lab_manager", "blis_lab_tech"]}
          >
            {report.notifications.length === 0 ? (
              <MenuItem>Report sent</MenuItem>
            ) : (
              <MenuSubTrigger>
                <MenuItem>Report sent</MenuItem>
                {/* <Dropdown.SubTrigger className="ml-0 pl-0"></Dropdown.SubTrigger> */}
                <Menu aria-label="Report notifications">
                  {report.notifications.map((notification) => (
                    <MenuItem
                      key={notification.id}
                      isDisabled
                      className="items-start [&>svg]:mt-0.5 [&_[slot=description]]:text-xs"
                    >
                      {notification.status === "SUCCESS" ? (
                        <CheckCircle2Icon className="text-green-600!" />
                      ) : notification.status === "PENDING" ? (
                        <StatusIcon status="in_progress" />
                      ) : (
                        <XCircleIcon className="siztext-red-600" />
                      )}
                      <div className="flex flex-col">
                        <Text slot="label">{notification.recipient}</Text>
                        <Text slot="description">
                          {dayjs(notification.createdAt).fromNow()}
                        </Text>
                      </div>
                    </MenuItem>
                  ))}
                </Menu>
              </MenuSubTrigger>
            )}

            <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
              <MenuItem onAction={() => setDialog("sendReport")}>
                <SendIcon />
                {report.notifications.length === 0 ? "Send" : "Re-send"}
              </MenuItem>
            </RoleCondition>
            <MenuSeparator />
          </RoleCondition>
          <MenuItem
            onAction={() => {
              window.open(report.apiUrl, "_blank");
            }}
          >
            <ExternalLinkIcon />
            Open
          </MenuItem>
          <MenuItem
            onAction={() => {
              if (report.filePath) {
                const name =
                  report.type === "upload"
                    ? report.name
                    : report.filePath.split("/").pop();

                const link = document.createElement("a");
                link.href = report.apiUrl;
                link.download = name || "report";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            }}
          >
            <DownloadIcon />
            Download
          </MenuItem>
          <PrintReportItem report={report} />
          <RoleCondition roles={["blis_lab_manager"]}>
            <MenuSeparator />
            <MenuItem
              isDanger
              isDisabled={report.type === "report" && report.isNotified}
              onAction={() => {
                setDialog("deleteReport");
              }}
            >
              <TrashIcon />
              Delete
            </MenuItem>
          </RoleCondition>
        </Menu>
      </MenuTrigger>
      <Modal
        isOpen={dialog === "sendReport"}
        onOpenChange={onDialogClose}
        aria-label="Send report"
      >
        <SendReportDialog report={report} />
      </Modal>
      <ConfirmPrompt
        open={dialog === "deleteReport"}
        onOpenChange={onDialogClose}
        danger
        loading={deleteReport.isPending}
        title="Delete report?"
        message="Are you sure you want to delete this report?"
        onConfirm={() => {
          if (report.type === "report") {
            deleteReport.mutate({ reportId: report.id });
          } else {
            deleteUpload.mutate(report.id);
          }
        }}
      />
    </>
  );
}

function PrintReportItem({ report }: { report: CombinedReport }) {
  const { print } = usePrintIframe(
    report.type === "report"
      ? `/api/data/reports/${report.filePath}`
      : `/api/data/uploads/${report.filePath}`,
    15000,
  );

  return (
    <MenuItem onAction={() => print()}>
      <PrinterIcon />
      Print
    </MenuItem>
  );
}

function SendReportDialog({ report }: { report: CombinedReport }) {
  const [recipients, setRecipients] = useState<{ email: string }[]>([]);
  const { setOpen } = useDialogState();
  const send = trpc.reports.sendNotifications.useMutation({
    onSuccess(data) {
      if (data.length === 0 || data[0].status === "SKIPPED") {
        toast.success("No recipients configured for report notification");
      }

      const success = data?.filter((r) => r.status === "SUCCESS").length ?? 0;
      const failed = data?.filter((r) => r.status === "FAILED").length ?? 0;
      const pending = data?.filter((r) => r.status === "PENDING").length ?? 0;

      const successMessage = success ? `${success} sent` : "";
      const failedMessage = failed ? `${failed} failed` : "";
      const pendingMessage = pending ? `${pending} pending` : "";

      let description = [successMessage, failedMessage, pendingMessage]
        .filter(Boolean)
        .join(", ");

      description = description.replace(/^(\d+) /, "$1 notifications ");

      toast.success("Report notifications sent", {
        description,
      });
      setOpen(false);
    },
    onError(error) {
      toast.error("Failed to send report", {
        description: error.message,
      });
    },
  });

  const { data } = trpc.notifications.getRecipients.useQuery({
    type: "REPORT",
    accessionId: report.accessionId ?? 0,
  });

  const allRecipients = data || [];

  useEffect(() => {
    if (data) {
      setRecipients(data);
    }
  }, [data]);

  return (
    <Dialog>
      <DialogHeader>
        <DialogTitle>
          {report.notifications.length === 0 ? "Send" : "Re-send"} report
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <Label id="select-recipients">Select recipients</Label>
        <GridList
          aria-labelledby="select-recipients"
          items={allRecipients || []}
          selectionMode="multiple"
          selectedKeys={recipients.map((r) => r.email)}
          aria-label="Selected tests"
          onSelectionChange={(keys) => {
            const emails =
              keys === "all"
                ? allRecipients
                : Array.from(keys).map((k) => ({ email: k.toString() }));
            setRecipients(emails);
          }}
        >
          {(item) => (
            <GridListItem
              key={item.email}
              id={item.email}
              textValue={item.email}
            >
              <Text>{item.email}</Text>
            </GridListItem>
          )}
        </GridList>
      </DialogBody>
      <DialogFooter>
        <Button
          isDisabled={recipients.length === 0 || send.isPending}
          isPending={send.isPending}
          onPress={() => {
            send.mutateAsync({
              reportId: report.type === "report" ? report.id : undefined,
              uploadId: report.type === "upload" ? report.id : undefined,
              recipients: recipients.map((r) => r.email),
            });
          }}
        >
          Send
        </Button>
        <DialogCloseButton>Cancel</DialogCloseButton>
      </DialogFooter>
    </Dialog>
  );
}

function useReports(accession: { id: number }): CombinedReport[] {
  const reports = trpc.accession.reports.useQuery(accession.id);
  const uploads = trpc.accession.uploads.list.useQuery(accession.id);

  const allReports = [
    ...(reports.data || []).map((r) => ({
      ...r,
      type: "report" as const,
      apiUrl: getApiUrl(`/api/data/reports/${r.filePath}`),
    })),
    ...(uploads.data || [])
      .filter((u) => u.type === "REPORT")
      .map((u) => ({
        ...u,
        type: "upload" as const,
        apiUrl: getApiUrl(`/api/data/uploads${u.filePath}`),
      })),
  ].sort((a, b) => {
    return dayjs(b.createdAt).diff(dayjs(a.createdAt));
  });

  return allReports;
}
